import * as Headless from '@headlessui/react';
import { CloudArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';

export const FloatingPendingChangesBar = ({
  show,
  saving = false,
  onDiscard,
  onSave,
  saveLabel = 'Save and Deploy Changes',
  discardLabel = 'Discard pending changes',
  saveButtonType
}: {
  show: boolean;
  saving?: boolean;
  onDiscard: () => void;
  /** When provided, the save control is a button that calls this handler. Otherwise it submits the surrounding form. */
  onSave?: () => void;
  saveLabel?: string;
  discardLabel?: string;
  saveButtonType?: 'button' | 'submit';
}) => {
  const type = saveButtonType ?? (onSave ? 'button' : 'submit');

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed bottom-12 left-[calc(50%-100px)] z-10 flex gap-2 rounded-full border border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 shadow-xl lg:left-1/2"
          initial={{ opacity: 0, translateY: 100, scale: 0.9 }}
          animate={{ opacity: 1, translateY: 0, scale: 1 }}
          exit={{ opacity: 0, translateY: 100, scale: 0.9 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
        >
          <Headless.Button
            type="button"
            className={clsx(
              'flex flex-shrink-0 items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              'border border-dashed border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/10'
            )}
            disabled={saving}
            onClick={onDiscard}
          >
            <div>{discardLabel}</div>
            <div>
              <XMarkIcon className="size-3.5" />
            </div>
          </Headless.Button>
          <Headless.Button
            type={type}
            className={clsx(
              'flex flex-shrink-0 items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              !saving && 'border border-transparent bg-blue-600/80 text-white hover:bg-blue-600/90',
              saving && 'border border-transparent bg-blue-400/80 text-white hover:bg-blue-400/90'
            )}
            disabled={saving}
            onClick={type === 'button' ? onSave : undefined}
          >
            <CloudArrowDownIcon className="size-3.5" />
            {saveLabel}
            {saving && <Spinner size="size-3.5" color="white" />}
          </Headless.Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
